import { useMemo, useState, useEffect } from 'react'
import * as Yup from 'yup'

const INITIAL_FORM_STATE = {
	paymentType: 'bank_transfer' as 'bank_transfer' | 'credit-card' | 'promptpay',
	evidence: undefined as File | undefined,
}

const ViewModel = ({
	isOpen,
	onSubmit,
}: {
	isOpen: boolean
	onSubmit: (
		paymentType: 'bank_transfer' | 'credit-card' | 'promptpay',
		evidence?: File
	) => Promise<boolean>
}) => {
	const [step, setStep] = useState<number>(1)
	const [formState, setFormState] = useState(INITIAL_FORM_STATE)
	const [formError, setFormError] = useState<any>({})
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

	const showPromptPay = useMemo(() => {
		return formState.paymentType === 'promptpay' && step === 2
	}, [formState.paymentType, step])

	const validationSchema = Yup.object().shape({
		evidence: Yup.mixed()
			.test('testEvidence', 'กรุณาอัพโหลดหลักฐานการโอน', value => {
				if (formState.paymentType === 'bank_transfer') {
					return value !== null
				} else {
					return true
				}
			})
			.required('กรุณาอัพโหลดหลักฐานการโอน'),
	})

	const heading = useMemo(() => {
		if (step === 1) {
			return {
				title: 'เลือกวิธีการชำระเงิน',
				description: 'เลือกวิธีการชำระเงินที่คุณต้องการ',
			}
		} else if (step === 2) {
			if (formState.paymentType === 'bank_transfer') {
				return {
					title: 'โอนเงินชำระ',
					description: 'อัพโหลดหลักฐานการโอน',
				}
			} else if (formState.paymentType === 'credit-card') {
				return {
					title: 'ชำระด้วยบัตรเครดิต',
					description: 'ระบบจะนำท่านไปยังหน้าชำระเงินด้วยบัตรเครดิต',
				}
			} else if (formState.paymentType === 'promptpay') {
				return {
					title: 'ชำระด้วย PromptPay',
					description: 'สแกน QR Code หรือใช้เบอร์โทรศัพท์ที่ลงทะเบียนกับ PromptPay',
				}
			} else {
				return {
					title: '',
					description: '',
				}
			}
		} else {
			return {
				title: '',
				description: '',
			}
		}
	}, [formState, step])

	const onSelectMethod = (method: 'bank_transfer' | 'credit-card' | 'promptpay') => {
		if (isSubmitting) return

		setFormState({ ...formState, paymentType: method })

		// Simulate credit card payment
		if (method === 'credit-card') {
			setIsSubmitting(true)
			onSubmit('credit-card').then(submitStatus => {
				if (!submitStatus) {
					onClose()
				} else {
					setStep(2)
				}

				setIsSubmitting(false)
			})
		}
		// Simulate prompt pay payment
		else if (method === 'promptpay') {
			setIsSubmitting(true)
			onSubmit('promptpay').then(submitStatus => {
				if (!submitStatus) {
					onClose()
				} else {
					setStep(2)
				}

				setIsSubmitting(false)
			})
		} else {
			setStep(2)
		}
	}

	const onUploadEvidence = (evidence: File | undefined) => {
		setFormState({ ...formState, evidence })
	}

	const onClose = () => {
		setTimeout(() => {
			setStep(1)

			setFormState(INITIAL_FORM_STATE)
			setFormError({})
		}, 1000)
	}

	const onBackStep = () => {
		setStep(1)
	}

	const submit = () => {
		validationSchema
			.validate(formState, { abortEarly: false })
			.then(() => {
				if (formState.paymentType === 'bank_transfer') {
					onSubmit(formState.paymentType, formState.evidence)
				} else {
					onSubmit(formState.paymentType, undefined)
				}
			})
			.catch(err => {
				const error: any = {}
				err.inner.forEach((e: any) => {
					error[e.path] = e.message
				})
				setFormError(error)
			})
	}

	useEffect(() => {
		if (step === 1 || !isOpen) {
			setTimeout(() => {
				setFormState(INITIAL_FORM_STATE)
				setFormError({})
			}, 500)
		}
	}, [step, isOpen])

	return {
		showPromptPay,
		step,
		formState,
		formError,
		heading,
		onSelectMethod,
		onUploadEvidence,
		onClose,
		onBackStep,
		submit,
	}
}

export default ViewModel
